import React from "react";
import styled from "styled-components";
import { notificationHelpers } from "../../services/notificationApi";

const NotificationItemWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.read ? "white" : "#fefce8")};

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 1rem;
  flex-shrink: 0;

  &.INFO {
    background: #e0f2fe;
    color: #0369a1;
  }

  &.SUCCESS {
    background: #dcfce7;
    color: #166534;
  }

  &.WARNING {
    background: #fef3c7;
    color: #92400e;
  }

  &.ERROR {
    background: #fee2e2;
    color: #991b1b;
  }

  /* Module-based styling */
  &.ASSESSMENT {
    background: #e0f2fe;
    color: #0369a1;
  }

  &.OKR {
    background: #dcfce7;
    color: #166534;
  }

  &.FEEDBACK {
    background: #fef3c7;
    color: #92400e;
  }

  &.MEETING {
    background: #e0e7ff;
    color: #4338ca;
  }

  &.SYSTEM {
    background: #f3e8ff;
    color: #7c3aed;
  }
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
`;

const NotificationMessage = styled.p`
  margin: 0 0 8px 0;
  font-size: 0.8rem;
  color: #475569;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NotificationMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const NotificationTime = styled.span`
  font-size: 0.7rem;
  color: #94a3b8;
`;

const NotificationPriority = styled.span`
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 500;

  &.high {
    background: #fee2e2;
    color: #991b1b;
  }

  &.medium {
    background: #fef3c7;
    color: #92400e;
  }

  &.low {
    background: #f0f9ff;
    color: #0369a1;
  }
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 8px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${NotificationItemWrapper}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #64748b;
  }

  &.danger:hover {
    background: #fee2e2;
    color: #dc2626;
  }
`;

const ModuleBadge = styled.span`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.65rem;
  font-weight: 500;
  background: #f1f5f9;
  color: #64748b;
  margin-left: 8px;
`;

const getModuleIcon = (module) => {
  const icons = {
    ASSESSMENT: "📋",
    OKR: "🎯",
    FEEDBACK: "💬",
    MEETING: "👥",
    SYSTEM: "⚙️",
  };
  return icons[module] || "📢";
};

const getModuleName = (module) => {
  const names = {
    ASSESSMENT: "Avaliação",
    OKR: "OKR",
    FEEDBACK: "Feedback",
    MEETING: "Reunião",
    SYSTEM: "Sistema",
  };
  return names[module] || module;
};

const NotificationItem = ({
  notification,
  onNotificationClick,
  onMarkAsRead,
  onDelete,
  showActions = true,
  showModule = true,
}) => {
  const {
    id,
    title,
    message,
    type,
    isRead,
    createdAt,
    data = {},
  } = notification;

  const module = data.module;
  const priority = notificationHelpers.getPriority(notification);
  const icon = data.module ? getModuleIcon(data.module) : notificationHelpers.getIcon(type);
  const iconClass = data.module || type;

  const handleClick = () => {
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
  };

  const handleMarkAsRead = (e) => {
    e.stopPropagation();
    if (onMarkAsRead) {
      onMarkAsRead(id);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(id);
    }
  };

  return (
    <NotificationItemWrapper
      read={isRead}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <NotificationIcon className={iconClass}>
        {icon}
      </NotificationIcon>

      <NotificationContent>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <NotificationTitle>
            {title}
            {!isRead && (
              <span
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#ef4444',
                  borderRadius: '50%',
                  marginLeft: '8px',
                }}
              />
            )}
          </NotificationTitle>
          {showModule && module && (
            <ModuleBadge>{getModuleName(module)}</ModuleBadge>
          )}
        </div>

        <NotificationMessage>{message}</NotificationMessage>

        <NotificationMeta>
          <NotificationTime>
            {notificationHelpers.formatTime(createdAt)}
          </NotificationTime>
          {priority !== "low" && (
            <NotificationPriority className={priority}>
              {priority === 'high' ? 'Alta' : priority === 'medium' ? 'Média' : 'Baixa'}
            </NotificationPriority>
          )}
        </NotificationMeta>
      </NotificationContent>

      {showActions && (
        <NotificationActions>
          {!isRead && (
            <ActionButton
              onClick={handleMarkAsRead}
              title="Marcar como lida"
              aria-label="Marcar como lida"
            >
              ✓
            </ActionButton>
          )}
          <ActionButton
            onClick={handleDelete}
            title="Excluir notificação"
            aria-label="Excluir notificação"
            className="danger"
          >
            ×
          </ActionButton>
        </NotificationActions>
      )}
    </NotificationItemWrapper>
  );
};

export default NotificationItem;